import type { TourDetails } from './_models'

export function normalizeIdList(items: unknown[] | undefined): string[] {
  return (items || [])
    .map((item) => {
      if (item == null) return ''
      if (typeof item === 'object') {
        const obj = item as { _id?: string; id?: string }
        return String(obj._id || obj.id || '')
      }
      return String(item)
    })
    .filter((id) => id && id !== 'undefined')
    .sort()
}

export const TOUR_TRANSLATION_CONTENT_FIELDS = [
  'title',
  'description',
  'shortDescription',
  'longDescription',
  'note',
  'cardBadge',
] as const

export const TOUR_TRANSLATION_SEO_FIELDS = [
  'seoSlug',
  'metaTitle',
  'metaDescription',
  'metaKeywords',
  'openGraphTitle',
  'openGraphDescription',
  'openGraphImage',
  'openGraphType',
  'openGraphLocale',
  'twitterTitle',
  'twitterDescription',
  'twitterImage',
  'twitterCardType',
  'metaRobots',
  'canonicalUrl',
  'structuredData',
] as const

export const TOUR_TRANSLATION_ALL_FIELDS = [
  ...TOUR_TRANSLATION_CONTENT_FIELDS,
  ...TOUR_TRANSLATION_SEO_FIELDS,
] as const

export type TourTranslationRef = {
  language?: string
  _id?: string
  title?: string
}

export function normalizeLang(language: unknown): string {
  return String(language ?? '').trim().toLowerCase()
}

export function findTranslationByLanguage(
  translations: Array<TourTranslationRef> | undefined,
  language: string,
): TourTranslationRef | undefined {
  const lc = normalizeLang(language)
  if (!lc) return undefined
  return (translations || []).find((t) => normalizeLang(t.language) === lc)
}

export function hasTourTranslationChanged(
  current: Record<string, unknown> | undefined,
  original: Record<string, unknown> | undefined,
): boolean {
  if (!original && !current) return false
  if (!original && current) return true

  for (const field of TOUR_TRANSLATION_CONTENT_FIELDS) {
    if ((current?.[field] ?? '') !== (original?.[field] ?? '')) {
      return true
    }
  }
  return false
}

export function resolveTourTranslationTitle(
  current: Record<string, unknown> | undefined,
  allTranslations: Array<TourTranslationRef> | undefined,
  selectedLanguage: string,
): string {
  const fromCurrent = typeof current?.title === 'string' ? current.title.trim() : ''
  if (fromCurrent) return fromCurrent

  for (const lang of [selectedLanguage, 'fr', 'en', 'de']) {
    const title = findTranslationByLanguage(allTranslations, lang)?.title?.trim()
    if (title) return title
  }
  return 'Tour'
}

function pickTranslationText(
  current: Record<string, unknown>,
  original: Record<string, unknown> | undefined,
  field: string,
): string {
  const cur = current[field]
  if (typeof cur === 'string' && cur.trim()) return cur
  const orig = original?.[field]
  if (typeof orig === 'string' && orig.trim()) return orig
  return typeof cur === 'string' ? cur : ''
}

export function buildTourTranslationPayload(
  values: Pick<TourDetails, 'translations' | 'tour_id' | 'id'>,
  selectedLanguage: string,
  allTranslations: Array<TourTranslationRef> | undefined,
  originalTranslationId?: string,
) {
  const current = (values.translations?.[0] || {}) as Record<string, unknown>
  const original = findTranslationByLanguage(
    allTranslations,
    selectedLanguage,
  ) as Record<string, unknown> | undefined

  return {
    _id: originalTranslationId || (original?._id as string | undefined) || undefined,
    tour_id: String(values.tour_id || values.id || ''),
    language: selectedLanguage,
    title: resolveTourTranslationTitle(current, allTranslations, selectedLanguage),
    description: pickTranslationText(current, original, 'description'),
    content:
      pickTranslationText(current, original, 'content') ||
      pickTranslationText(current, original, 'description'),
    shortDescription: pickTranslationText(current, original, 'shortDescription'),
    longDescription: pickTranslationText(current, original, 'longDescription'),
    note: pickTranslationText(current, original, 'note'),
    cardBadge:
      typeof current.cardBadge === 'string'
        ? current.cardBadge.trim().slice(0, 80)
        : typeof original?.cardBadge === 'string'
          ? String(original.cardBadge)
          : '',
  }
}

export function buildTourTranslationSeoPayload(
  translation: Record<string, unknown>,
  selectedLanguage: string,
) {
  return {
    language: selectedLanguage,
    seoSlug: (translation.seoSlug as string) ?? '',
    metaTitle: (translation.metaTitle as string) ?? '',
    metaDescription: (translation.metaDescription as string) ?? '',
    metaKeywords: (translation.metaKeywords as string[]) ?? [],
    openGraphTitle: (translation.openGraphTitle as string) ?? '',
    openGraphDescription: (translation.openGraphDescription as string) ?? '',
    openGraphImage: (translation.openGraphImage as string) ?? '',
    openGraphType: (translation.openGraphType as string) ?? 'website',
    openGraphLocale: (translation.openGraphLocale as string) ?? selectedLanguage,
    twitterTitle: (translation.twitterTitle as string) ?? '',
    twitterDescription: (translation.twitterDescription as string) ?? '',
    twitterImage: (translation.twitterImage as string) ?? '',
    twitterCardType: (translation.twitterCardType as string) ?? 'summary_large_image',
    metaRobots: (translation.metaRobots as string) ?? 'index,follow',
    canonicalUrl: (translation.canonicalUrl as string) ?? '',
    structuredData: (translation.structuredData as string) ?? '',
  }
}
